import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";
import type { ComponentProps } from "react";

import { Button } from "components/core/Button";
import type { ButtonProps } from "components/core/Button";
import { DEFAULT_PAGE_SIZE } from "consts";
import { cn } from "helpers/cn";

const Pagination = ({ className, ...props }: ComponentProps<"nav">) => (
  <nav
    aria-label="pagination"
    className={cn("mx-auto flex w-full justify-center", className)}
    data-slot="pagination"
    {...props}
  />
);

const PaginationContent = ({ className, ...props }: ComponentProps<"ul">) => (
  <ul
    className={cn("m-0 flex list-none flex-row items-center gap-1 p-0", className)}
    data-slot="pagination-content"
    {...props}
  />
);

const PaginationItem = (props: ComponentProps<"li">) => (
  <li data-slot="pagination-item" {...props} />
);

type PaginationLinkProps = Omit<ComponentProps<typeof Button>, "variant" | "color"> & {
  isActive?: boolean;
};

const PaginationLink = ({
  isActive,
  shape = "square",
  ...props
}: PaginationLinkProps) => (
  <Button
    aria-current={isActive ? "page" : undefined}
    color={isActive ? "primary" : "neutral"}
    data-active={isActive}
    data-slot="pagination-link"
    shape={shape}
    variant={isActive ? "outlined" : "text"}
    {...props}
  />
);

const PaginationPrevious = ({ className, ...props }: PaginationLinkProps) => (
  <PaginationLink
    aria-label="Go to previous page"
    className={cn("gap-1 px-2.5", className)}
    shape="default"
    {...props}
  >
    <ChevronLeft size={16} />
    <span className="hidden md:block">Previous</span>
  </PaginationLink>
);

const PaginationNext = ({ className, ...props }: PaginationLinkProps) => (
  <PaginationLink
    aria-label="Go to next page"
    className={cn("gap-1 px-2.5", className)}
    shape="default"
    {...props}
  >
    <span className="hidden md:block">Next</span>
    <ChevronRight size={16} />
  </PaginationLink>
);

const PaginationEllipsis = ({ className, ...props }: ComponentProps<"span">) => (
  <span
    className={cn("flex w-8 items-center justify-center", className)}
    data-slot="pagination-ellipsis"
    {...props}
  >
    <MoreHorizontal aria-hidden size={16} />
    <span className="sr-only">More pages</span>
  </span>
);

const SIBLING_COUNT = 2;

const MAX_ITEMS = 2 * SIBLING_COUNT + 5;

const range = (start: number, end: number) =>
  Array.from({ length: end - start + 1 }, (_, offset) => start + offset);

const getPageItems = (current: number, pageCount: number): (number | "ellipsis")[] => {
  if (pageCount <= MAX_ITEMS) return range(1, pageCount);

  const start = Math.max(
    Math.min(current - SIBLING_COUNT, pageCount - 2 * SIBLING_COUNT - 2),
    3
  );
  const end = Math.min(
    Math.max(current + SIBLING_COUNT, 2 * SIBLING_COUNT + 3),
    pageCount - 2
  );

  return [
    1,
    start > 3 ? "ellipsis" : 2,
    ...range(start, end),
    end < pageCount - 2 ? "ellipsis" : pageCount - 1,
    pageCount
  ];
};

type PaginationControlProps = {
  current: number;
  hideOnSinglePage?: boolean;
  onChange: (page: number) => void;
  pageSize?: number;
  showTotal?: boolean;
  size?: ButtonProps["size"];
  total: number;
};

const PaginationControl = ({
  current: requestedPage,
  hideOnSinglePage,
  onChange,
  pageSize = DEFAULT_PAGE_SIZE,
  showTotal,
  size,
  total
}: PaginationControlProps) => {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(requestedPage, pageCount);

  if (hideOnSinglePage && pageCount <= 1) return null;

  const rangeStart = total === 0 ? 0 : (current - 1) * pageSize + 1;
  const rangeEnd = Math.min(current * pageSize, total);

  return (
    <Pagination>
      <PaginationContent>
        {showTotal && (
          <PaginationItem className="mr-2 text-body-small">
            {rangeStart}-{rangeEnd} of {total}
          </PaginationItem>
        )}
        <PaginationItem>
          <PaginationPrevious
            disabled={current <= 1}
            size={size}
            onClick={() => onChange(current - 1)}
          />
        </PaginationItem>
        {getPageItems(current, pageCount).map((item, index) => (
          <PaginationItem key={item === "ellipsis" ? `ellipsis-${index}` : item}>
            {item === "ellipsis" ? (
              <PaginationEllipsis />
            ) : (
              <PaginationLink
                isActive={item === current}
                size={size}
                onClick={() => onChange(item)}
              >
                {item}
              </PaginationLink>
            )}
          </PaginationItem>
        ))}
        <PaginationItem>
          <PaginationNext
            disabled={current >= pageCount}
            size={size}
            onClick={() => onChange(current + 1)}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
};

export {
  Pagination,
  PaginationContent,
  PaginationControl,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious
};
